import { useState } from 'react';
import { API } from '../contexts/AuthContext';

/**
 * Miniatura da foto do produto com fallback gracioso.
 *
 * Aponta um <img> para /produtos/:codpro/imagem/:ordem (proxy público do backend).
 * Se a imagem não existir, vira um placeholder (ícone de caixa).
 * Se `clicavel`, o clique amplia a foto num modal.
 *
 * Props:
 *   - codpro: string (obrigatório)
 *   - ordem: número (1 = primeira foto; default 1)
 *   - tamanho: número em px (default 48)
 *   - clicavel: boolean (default false)
 *   - alt: string
 */
export default function ImagemProduto({ codpro, ordem = 1, tamanho = 48, clicavel = false, alt, className = '' }) {
  const [erro, setErro] = useState(false);
  const [ampliado, setAmpliado] = useState(false);

  const cod = codpro ? String(codpro).trim() : '';
  const src = cod ? `${API}/produtos/${cod}/imagem/${ordem}` : null;

  // Baixa a foto ampliada (fetch como blob pra forçar download mesmo cross-origin da API).
  const baixar = async (e) => {
    e.stopPropagation();
    try {
      const res = await fetch(src);
      const blob = await res.blob();
      const ext = (blob.type.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `produto-${cod}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      window.open(src, '_blank'); // fallback: abre em nova aba pra salvar manualmente
    }
  };

  // Sem codpro OU erro de carregamento → placeholder
  if (!src || erro) {
    return (
      <div
        className={className}
        style={{
          width: tamanho, height: tamanho, borderRadius: 8,
          background: 'var(--mostarda-claro)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--mostarda)', fontSize: tamanho * 0.42, flexShrink: 0,
        }}
        title={alt || 'Sem foto disponível'}
        aria-label="Sem foto disponível"
      >
        <i className="bi bi-box-seam"></i>
      </div>
    );
  }

  return (
    <>
      <img
        src={src}
        alt={alt || `Foto do produto ${cod}`}
        loading="lazy"
        onError={() => setErro(true)}
        onClick={clicavel ? (e) => { e.stopPropagation(); setAmpliado(true); } : undefined}
        className={className}
        style={{
          width: tamanho, height: tamanho, objectFit: 'cover', borderRadius: 8,
          background: '#f7f7f7', border: '1px solid #eee',
          cursor: clicavel ? 'zoom-in' : 'default', flexShrink: 0,
        }}
      />

      {clicavel && ampliado && (
        <div
          onClick={(e) => { e.stopPropagation(); setAmpliado(false); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 99999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'zoom-out', padding: 24,
          }}
          role="dialog"
          aria-label="Imagem ampliada"
        >
          <img
            src={src}
            alt={alt || `Foto do produto ${cod}`}
            style={{ maxWidth: '95vw', maxHeight: '90vh', borderRadius: 12, boxShadow: '0 12px 40px rgba(0,0,0,0.5)', objectFit: 'contain' }}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={baixar}
            aria-label="Baixar foto"
            title="Baixar foto"
            style={{
              position: 'absolute', top: 20, right: 76,
              background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none',
              borderRadius: '50%', width: 44, height: 44, fontSize: '1.2rem', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <i className="bi bi-download"></i>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setAmpliado(false); }}
            aria-label="Fechar"
            style={{
              position: 'absolute', top: 20, right: 24,
              background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none',
              borderRadius: '50%', width: 44, height: 44, fontSize: '1.4rem', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
      )}
    </>
  );
}
